import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Save, Edit, Trash2, Check, Copy, Repeat, ChevronRight, Link2, Link2Off } from 'lucide-react';
import { toast } from 'sonner';
import {
  generateProductName, parseVirareFromName, parseIncomeFromName, parseAvansFromName,
} from './productName';
import { groupByZone, getZone, maxYears } from './productZones';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const numOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
};
const intOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseInt(String(v).replace(/[^\d]/g, ''), 10);
  return isNaN(n) ? null : n;
};

// Presetări avans (identice cu wizard-ul din BanksManagerV2)
const DP_PRESETS = [
  { key: 'unset', label: 'Nespecificat', min: null, max: null },
  { key: '15', label: '15% (15–20%)', min: 15, max: 20 },
  { key: 'lt20', label: '< 20% (0–20%)', min: 0, max: 20 },
  { key: 'gte20', label: '≥ 20% (20–100%)', min: 20, max: 100 },
  { key: 'gte25', label: '25% (25–100%)', min: 25, max: 100 },
  { key: '20_40', label: '20–40%', min: 20, max: 40 },
  { key: 'gt40', label: '≥ 40% (40–100%)', min: 40, max: 100 },
];
const dpPairToKey = (mn, mx) => {
  if (mn == null && mx == null) return 'unset';
  const hit = DP_PRESETS.find((p) => p.key !== 'unset' && p.min === mn && p.max === mx);
  return hit ? hit.key : 'unset';
};

const inp = 'w-full border rounded px-2 py-1.5 text-sm';
const sel = 'w-full border rounded px-2 py-1.5 text-sm bg-white';

// Câmp cu etichetă deasupra, cu lățime proprie
function Field({ label, w = 'w-32', children }) {
  return (
    <div className={`${w} flex flex-col gap-0.5`}>
      <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400">{label}</span>
      {children}
    </div>
  );
}

function initRow(p) {
  const name = p.product_name || p.name || '';
  const avans = { min: p.down_payment_min, max: p.down_payment_max };
  if (avans.min == null) {
    const parsed = parseAvansFromName(name);
    avans.min = parsed.min; avans.max = parsed.max;
  }
  return {
    credit_type: p.credit_type || 'achizitie',
    zone: getZone(p),
    fixed_years: p.fixed_years ?? 3,
    energy_class: p.energy_class || 'BC',
    salary_transfer: p.salary_transfer != null ? p.salary_transfer : parseVirareFromName(name),
    down_payment_min: avans.min ?? null,
    down_payment_max: avans.max ?? null,
    min_income_required: p.min_income_required ?? (p.max_income_allowed ? '' : (parseIncomeFromName(name) ?? '')),
    fixed_rate: p.fixed_rate ?? p.fixed_interest_base ?? '',
    variable_margin: p.variable_margin ?? p.variable_margin_base ?? '',
    currency: p.currency || 'RON',
    notes: p.notes || '',
  };
}


function ProductBlock({ product, bankName, bankProducts = [], onEdit, onDeleted, onSaved, onReload,
                        checked = false, onToggleSelect }) {
  const [row, setRow] = useState(() => initRow(product));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [propagate, setPropagate] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const original = useMemo(() => initRow(product), [product]);
  const dirty = JSON.stringify(row) !== JSON.stringify(original);

  const linkedRefis = useMemo(
    () => bankProducts.filter((p) => p.source_product_id === product.id),
    [bankProducts, product.id]
  );
  const sourceProduct = useMemo(
    () => bankProducts.find((p) => p.id === product.source_product_id) || null,
    [bankProducts, product.source_product_id]
  );
  const purchaseOptions = useMemo(
    () => bankProducts.filter((p) => p.credit_type !== 'refinantare'),
    [bankProducts]
  );

  const set = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  const isRefi = row.credit_type === 'refinantare';
  const isVariable = row.zone === 'variabila';
  const isFullFixed = row.zone === 'fixa';
  const isMixed = row.zone === 'mixta';
  const fullYears = maxYears(product);
  const effectiveFixedYears = isVariable ? null : (isFullFixed ? fullYears : intOrNull(row.fixed_years));

  const structured = {
    ...row,
    program: product.program,
    max_income_allowed: product.max_income_allowed,
    interest_type: isVariable ? 'variable' : 'fixed_then_variable',
    salary_transfer: row.salary_transfer,
    min_income_required: intOrNull(row.min_income_required),
    fixed_years: effectiveFixedYears,
    fixed_full_period: isFullFixed,
  };
  const autoName = generateProductName(structured, bankName);
  const variants = (product.rate_lookup || []).length;

  const changeZone = (zone) => setRow((r) => ({
    ...r,
    zone,
    // la „fixă pe toată perioada" perioada fixă devine durata maximă a produsului
    fixed_years: zone === 'fixa' ? fullYears : (zone === 'mixta' && Number(r.fixed_years) >= fullYears ? 3 : r.fixed_years),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fixed_rate = numOrNull(row.fixed_rate);
      const variable_margin = numOrNull(row.variable_margin);
      const payload = {
        product_name: autoName,
        credit_type: row.credit_type,
        interest_type: isVariable ? 'variable' : 'fixed_then_variable',
        fixed_years: isVariable ? 0 : effectiveFixedYears,
        energy_class: row.energy_class,
        salary_transfer: row.salary_transfer,
        down_payment_min: row.down_payment_min,
        down_payment_max: row.down_payment_max,
        min_income_required: intOrNull(row.min_income_required),
        currency: row.currency,
        notes: row.notes,
        fixed_rate, fixed_interest_base: fixed_rate,
        variable_margin, variable_margin_base: variable_margin,
        rate_lookup: [{ conditions: {}, fixed_rate, variable_margin }],
      };
      await axios.put(`${API_URL}/api/products/${product.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Propagare la refinanțările conectate (dobânzile doar dacă sincronizarea e activă pe legătură)
      if (propagate && linkedRefis.length > 0) {
        const res = await axios.post(`${API_URL}/api/products/${product.id}/propagate`,
          { update_data: payload, source_product_name: autoName },
          { headers: { Authorization: `Bearer ${token}` } });
        const n = (res.data.updated || []).length;
        toast.success(n === 1 ? 'Salvat · refinanțarea conectată a fost actualizată'
                             : `Salvat · ${n} refinanțări actualizate`);
        onSaved && onSaved(product.id, payload);
        onReload && onReload();
        return;
      }
      toast.success('Salvat');
      // Actualizare pe loc: fără reîncărcarea listei, fără pierderea poziției pe ecran
      onSaved && onSaved(product.id, payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/products/${product.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Produs duplicat (Copie)');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la duplicare');
    } finally {
      setBusy(false);
    }
  };

  const createRefi = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/products/bulk-refinance`, { product_ids: [product.id] }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Refinanțare creată și conectată la acest produs');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la creare refinanțare');
    } finally {
      setBusy(false);
    }
  };

  const setLink = async (body, message) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/products/${product.id}/link`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(message);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la modificarea legăturii');
    } finally {
      setBusy(false);
    }
  };

  const del = async (cascade) => {
    setDeleteOpen(false);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/products/${product.id}?cascade_refinance=${cascade ? 'true' : 'false'}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(cascade && linkedRefis.length
        ? `Produs și ${linkedRefis.length} refinanțare(ări) șterse`
        : 'Produs șters');
      onDeleted && onDeleted();
    } catch (e) {
      toast.error('Eroare la ștergere');
    }
  };

  const askDelete = () => {
    if (linkedRefis.length > 0) { setDeleteOpen(true); return; }
    if (window.confirm(`Ștergi produsul "${product.product_name || product.name}"?`)) del(false);
  };

  const rateMissing = !isVariable && (row.fixed_rate === '' || row.fixed_rate == null);
  const marginMissing = !isFullFixed && (row.variable_margin === '' || row.variable_margin == null);

  return (
    <div className="border rounded-lg bg-white hover:shadow-sm transition-shadow" data-testid={`product-row-${product.id}`}>
      {/* Antet bloc: denumire + acțiuni */}
      <div className="flex items-start justify-between gap-3 px-3 py-2 bg-slate-50 border-b rounded-t-lg">
        <div className="flex items-start gap-2 min-w-0">
          <Checkbox checked={checked} onCheckedChange={() => onToggleSelect && onToggleSelect(product.id)}
            className="mt-1" data-testid={`pt-select-${product.id}`} />
          <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800 leading-tight break-words">{autoName}</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className={`text-[10px] ${isRefi ? 'text-purple-600 border-purple-300' : 'text-emerald-600 border-emerald-300'}`}>
              {isRefi ? 'Refinanțare' : 'Achiziție'}
            </Badge>
            {variants > 0 && <Badge variant="outline" className="text-[10px] text-blue-600">{variants} variante</Badge>}
            {linkedRefis.length > 0 && (
              <Badge variant="outline" className="text-[10px] text-purple-700 border-purple-300 gap-1"
                data-testid={`pt-has-refi-${product.id}`}>
                <Link2 className="h-3 w-3" /> are refinanțare conectată
              </Badge>
            )}
            {sourceProduct && (
              <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300 gap-1"
                title={sourceProduct.product_name} data-testid={`pt-linked-${product.id}`}>
                <Link2 className="h-3 w-3" /> conectat cu: {sourceProduct.product_name}
              </Badge>
            )}
            {dirty && <span className="text-[10px] text-amber-600 font-medium">• modificări nesalvate</span>}
            {/de confirmat/i.test(product.notes || '') && (
              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400 bg-amber-50"
                title={product.notes} data-testid={`pt-to-confirm-${product.id}`}>
                de confirmat la bancă
              </Badge>
            )}
          </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {linkedRefis.length > 0 && (
            <label className="flex items-center gap-1 text-[10px] text-gray-600 mr-1 cursor-pointer"
              title="Aplică modificările și la refinanțarea conectată">
              <Checkbox checked={propagate} onCheckedChange={(v) => setPropagate(!!v)}
                data-testid={`pt-propagate-${product.id}`} />
              Aplică și la refinanțare
            </label>
          )}
          <Button size="sm" variant={dirty ? 'default' : 'ghost'} className="h-8 px-2" disabled={!dirty || saving}
            onClick={save} title="Salvează modificările" data-testid={`pt-save-${product.id}`}>
            {saving ? <Check className="h-4 w-4 animate-pulse" /> : <Save className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onEdit(product)}
            title="Editare avansată / variante" data-testid={`pt-edit-${product.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 px-2" disabled={busy} onClick={duplicate}
            title="Duplică (Copie)" data-testid={`pt-duplicate-${product.id}`}>
            <Copy className="h-4 w-4" />
          </Button>
          {!isRefi && (
            <Button size="sm" variant="ghost" className="h-8 px-2 text-purple-600" disabled={busy || linkedRefis.length > 0}
              onClick={createRefi}
              title={linkedRefis.length > 0 ? 'Are deja o refinanțare conectată' : 'Creează refinanțare identică (conectată)'}
              data-testid={`pt-refi-${product.id}`}>
              <Repeat className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" onClick={askDelete}
            title="Șterge" data-testid={`pt-delete-${product.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Legătura achiziție ↔ refinanțare (doar pe produsele de refinanțare) */}
      {isRefi && (
        <div className="flex flex-wrap items-end gap-3 px-3 pt-2 pb-1 bg-purple-50/40 border-b">
          <Field label="Conectat cu produsul de achiziție" w="w-80">
            <select className={sel} value={product.source_product_id || ''} disabled={busy}
              onChange={(e) => setLink({ source_product_id: e.target.value || null },
                e.target.value ? 'Legătură salvată' : 'Legătură ruptă')}
              data-testid={`pt-link-select-${product.id}`}>
              <option value="">— neconectat —</option>
              {purchaseOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.product_name}</option>
              ))}
            </select>
          </Field>
          {product.source_product_id && (
            <>
              <label className="flex items-center gap-2 text-xs text-gray-700 pb-1 cursor-pointer">
                <Checkbox checked={product.sync_interest !== false} disabled={busy}
                  onCheckedChange={(v) => setLink({ sync_interest: !!v },
                    v ? 'Dobânzile se vor sincroniza' : 'Dobânzile NU se vor mai sincroniza')}
                  data-testid={`pt-sync-interest-${product.id}`} />
                Sincronizează și dobânzile (rată fixă și marjă)
              </label>
              <Button size="sm" variant="ghost" className="h-8 px-2 text-gray-500 pb-1" disabled={busy}
                onClick={() => setLink({ source_product_id: null }, 'Legătură ruptă')}
                title="Rupe legătura" data-testid={`pt-unlink-${product.id}`}>
                <Link2Off className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid={`pt-delete-dialog-${product.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Produsul are {linkedRefis.length} refinanțare(ări) conectate</AlertDialogTitle>
            <AlertDialogDescription>
              {product.product_name || product.name}
              <br />
              Ce facem cu refinanțările conectate: {linkedRefis.map((r) => r.product_name).join(', ')}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`pt-delete-cancel-${product.id}`}>Renunță</AlertDialogCancel>
            <Button variant="outline" onClick={() => del(false)} data-testid={`pt-delete-keep-${product.id}`}>
              Șterge doar acest produs (rupe legătura)
            </Button>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(true)}
              data-testid={`pt-delete-cascade-${product.id}`}>
              Șterge și refinanțările
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Câmpuri editabile, fiecare cu etichetă deasupra */}
      <div className="flex flex-wrap gap-x-3 gap-y-2 px-3 py-3">
        <Field label="Tip credit" w="w-36">
          <select className={sel} value={row.credit_type} onChange={(e) => set('credit_type', e.target.value)} data-testid={`pt-credit_type-${product.id}`}>
            <option value="achizitie">Achiziție</option>
            <option value="refinantare">Refinanțare</option>
          </select>
        </Field>
        <Field label="Dobândă" w="w-56">
          <select className={sel} value={row.zone} onChange={(e) => changeZone(e.target.value)}
            data-testid={`pt-zone-${product.id}`}>
            <option value="variabila">Variabilă toată perioada</option>
            <option value="fixa">Fixă toată perioada</option>
            <option value="mixta">Mixtă – Fixă + variabilă</option>
          </select>
        </Field>
        {isMixed && (
          <Field label="Perioadă fixă" w="w-28">
            <select className={sel} value={row.fixed_years} onChange={(e) => set('fixed_years', e.target.value)}
              data-testid={`pt-fixed_years-${product.id}`}>
              {[2, 3, 5, 10].map((y) => <option key={y} value={y}>{y} ani</option>)}
            </select>
          </Field>
        )}
        <Field label="Clasă energ." w="w-28">
          <select className={sel} value={row.energy_class} onChange={(e) => set('energy_class', e.target.value)}>
            <option value="BC">B / C</option>
            <option value="A">Clasa A</option>
          </select>
        </Field>
        <Field label="Virare salariu" w="w-36">
          <select className={sel} value={row.salary_transfer === true ? 'cu' : row.salary_transfer === false ? 'fara' : ''}
            onChange={(e) => set('salary_transfer', e.target.value === 'cu' ? true : e.target.value === 'fara' ? false : null)}
            data-testid={`pt-virare-${product.id}`}>
            <option value="">— nespecificat</option>
            <option value="cu">Cu virare</option>
            <option value="fara">Fără virare</option>
          </select>
        </Field>
        <Field label="Avans (LTV)" w="w-44">
          <select className={sel} value={dpPairToKey(row.down_payment_min, row.down_payment_max)}
            onChange={(e) => {
              const p = DP_PRESETS.find((x) => x.key === e.target.value);
              setRow((r) => ({ ...r, down_payment_min: p ? p.min : null, down_payment_max: p ? p.max : null }));
            }}
            data-testid={`pt-avans-${product.id}`}>
            {DP_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Venit min (lei)" w="w-32">
          <input className={inp} type="number" value={row.min_income_required} placeholder="ex. 5000"
            onChange={(e) => set('min_income_required', e.target.value)} />
        </Field>
        <Field label="Rată fixă %" w="w-24">
          <input className={`${inp} ${rateMissing ? 'border-red-400 bg-red-50' : ''}`} value={row.fixed_rate}
            disabled={isVariable} placeholder="%" onChange={(e) => set('fixed_rate', e.target.value)}
            data-testid={`pt-fixed_rate-${product.id}`} />
        </Field>
        <Field label="Marjă %" w="w-24">
          <input className={`${inp} ${marginMissing ? 'border-red-400 bg-red-50' : ''}`} value={row.variable_margin}
            disabled={isFullFixed} placeholder="%" onChange={(e) => set('variable_margin', e.target.value)} />
        </Field>
        <Field label="Monedă" w="w-24">
          <select className={sel} value={row.currency} onChange={(e) => set('currency', e.target.value)}>
            <option value="RON">RON</option>
            <option value="EUR">EUR</option>
          </select>
        </Field>
        <Field label="Mențiuni" w="flex-1 min-w-[180px]">
          <input className={inp} value={row.notes} placeholder="mențiuni interne…" onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
    </div>
  );
}

const ZONE_STORE_KEY = 'finbro_admin_zones_collapsed';
const loadCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(ZONE_STORE_KEY) || '{}'); } catch { return {}; }
};

export default function ProductsTable({ products = [], bank = {}, bankProducts = [], onEdit, onDeleted, onSaved, onReload }) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const zones = useMemo(() => groupByZone(products), [products]);
  const allProducts = bankProducts.length ? bankProducts : products;

  const toggle = (zoneKey) => {
    const id = `${bank.id}:${zoneKey}`;
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(ZONE_STORE_KEY, JSON.stringify(next)); } catch { /* ignoră */ }
      return next;
    });
  };

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleZoneSelect = (zoneProducts) => {
    const ids = zoneProducts.map((p) => p.id);
    const allIn = ids.every((id) => selected.includes(id));
    setSelected((prev) => (allIn ? prev.filter((id) => !ids.includes(id))
                                 : [...new Set([...prev, ...ids])]));
  };

  const bulkRefinance = async () => {
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/products/bulk-refinance`,
        { product_ids: selected }, { headers: { Authorization: `Bearer ${token}` } });
      const { created = [], skipped = [] } = res.data;
      toast.success(`${created.length} refinanțări create${skipped.length ? `, ${skipped.length} sărite` : ''}`);
      if (skipped.length) {
        const motive = [...new Set(skipped.map((s) => s.reason))].join('; ');
        toast.message(`Sărite: ${motive}`);
      }
      setSelected([]);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la crearea refinanțărilor');
    } finally {
      setBulkBusy(false);
    }
  };

  if (products.length === 0) {
    return <p className="text-gray-400 text-sm italic">Niciun produs pentru filtrul selectat</p>;
  }

  return (
    <div className="space-y-3" data-testid={`products-table-${bank.id}`}>
      {selected.length > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg border border-purple-300 bg-purple-50 shadow-sm"
          data-testid={`bulk-bar-${bank.id}`}>
          <span className="text-sm font-semibold text-purple-900" data-testid={`bulk-count-${bank.id}`}>
            {selected.length} {selected.length === 1 ? 'produs selectat' : 'produse selectate'}
          </span>
          <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-700" disabled={bulkBusy}
            onClick={bulkRefinance} data-testid={`bulk-refi-${bank.id}`}>
            <Repeat className="h-4 w-4 mr-1" /> Creează refinanțare pentru selecție
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelected([])}
            data-testid={`bulk-clear-${bank.id}`}>
            Anulează selecția
          </Button>
          <span className="text-[11px] text-purple-700">
            Produsele care au deja o refinanțare conectată sunt sărite automat
          </span>
        </div>
      )}
      {zones.map((zone) => {
        const isCollapsed = collapsed[`${bank.id}:${zone.key}`] || false;
        const zoneSelected = zone.products.length > 0 && zone.products.every((p) => selected.includes(p.id));
        return (
          <div key={zone.key} className={`border-l-4 ${zone.color} bg-slate-50/60 rounded-r-lg`}>
            <div className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-100/70 transition-colors rounded-r-lg">
              {zone.products.length > 0 && (
                <Checkbox checked={zoneSelected} onCheckedChange={() => toggleZoneSelect(zone.products)}
                  title="Selectează toate produsele din această subzonă"
                  data-testid={`zone-select-${bank.id}-${zone.key}`} />
              )}
              <button type="button" onClick={() => toggle(zone.key)}
                className="flex-1 flex items-center gap-2 text-left"
                data-testid={`zone-toggle-${bank.id}-${zone.key}`}>
                <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                <span className="text-sm font-semibold text-gray-700">{zone.label}</span>
                <Badge variant="outline" className="text-[10px]" data-testid={`zone-count-${bank.id}-${zone.key}`}>
                  {zone.products.length} {zone.products.length === 1 ? 'produs' : 'produse'}
                </Badge>
              </button>
            </div>
            {!isCollapsed && (
              <div className="px-2 pb-2 space-y-2" data-testid={`zone-body-${bank.id}-${zone.key}`}>
                {zone.products.length === 0 ? (
                  <p className="text-gray-400 text-xs italic px-1 py-1">Niciun produs în această categorie</p>
                ) : zone.products.map((p) => (
                  <ProductBlock key={p.id} product={p} bankName={bank.name || ''} bankProducts={allProducts}
                    onEdit={onEdit} onDeleted={onDeleted} onSaved={onSaved} onReload={onReload}
                    checked={selected.includes(p.id)} onToggleSelect={toggleSelect} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
